import type { ErrorResponse } from '../../../../shared/types/epics';
import { API_BASE } from './version';

export interface LoginResult {
  token: string;
  refreshToken?: string;
  expiresIn: number;
  user: {
    email: string;
  };
}

interface LoginResponseEnvelope {
  data: LoginResult;
}

/**
 * Authentication errors thrown by the auth API client. The `code` is
 * stable and machine-readable; UI components branch on it.
 */
export class AuthError extends Error {
  code: 'INVALID_CREDENTIALS' | 'INVALID_REQUEST' | 'INTERNAL_ERROR' | 'NETWORK';
  status: number;
  constructor(code: AuthError['code'], message: string, status: number) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
    this.status = status;
  }
}

/**
 * POST {API_BASE}/auth/login — exchanges email + password for a JWT.
 * Returns the token & user; throws AuthError on any non-2xx response.
 */
export async function login(email: string, password: string): Promise<LoginResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  } catch (err) {
    throw new AuthError(
      'NETWORK',
      err instanceof Error ? err.message : 'Network error',
      0
    );
  }

  if (!response.ok) {
    let code: AuthError['code'] = 'INTERNAL_ERROR';
    let message = response.statusText || 'Login failed';
    try {
      const body = (await response.json()) as ErrorResponse;
      message = body.error?.message ?? message;
      const c = body.error?.code;
      if (c === 'INVALID_REQUEST' || c === 'INVALID_CREDENTIALS' || c === 'INTERNAL_ERROR') {
        code = c as AuthError['code'];
      }
    } catch {
      /* response wasn't JSON */
    }
    throw new AuthError(code, message, response.status);
  }

  const body = (await response.json()) as LoginResponseEnvelope;
  return body.data;
}

/**
 * POST {API_BASE}/auth/logout — best-effort. JWTs are stateless, so the
 * primary work happens client-side (clearing the stored token).
 */
export async function logout(): Promise<void> {
  try {
    await fetch(`${API_BASE}/auth/logout`, { method: 'POST' });
  } catch {
    /* ignore — client clears local state regardless */
  }
}

// ---------------------------------------------------------------------------
// Signup / email confirmation
// ---------------------------------------------------------------------------

export interface SignupResult {
  userSub: string;
  /** 'EMAIL' | 'SMS' | 'NONE' (dev mode). */
  deliveryMedium: string;
  /** True if Cognito returned the user as already confirmed (rare). */
  userConfirmed: boolean;
}

interface SignupResponseEnvelope { data: SignupResult; }

/**
 * POST {API_BASE}/auth/signup — create a new account. Cognito sends a
 * 6-digit verification code to the provided email; the user enters it
 * via POST /auth/confirm.
 */
export async function signup(email: string, password: string): Promise<SignupResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  } catch (err) {
    throw new AuthError('NETWORK', err instanceof Error ? err.message : 'Network error', 0);
  }

  if (!response.ok) {
    let code: AuthError['code'] = 'INTERNAL_ERROR';
    let message = response.statusText || 'Signup failed';
    try {
      const body = (await response.json()) as ErrorResponse;
      message = body.error?.message ?? message;
      if (body.error?.code === 'INVALID_REQUEST') code = 'INVALID_REQUEST';
      if (body.error?.code === 'INVALID_CREDENTIALS') code = 'INVALID_CREDENTIALS';
    } catch { /* not JSON */ }
    throw new AuthError(code, message, response.status);
  }

  const body = (await response.json()) as SignupResponseEnvelope;
  return body.data;
}

/**
 * POST {API_BASE}/auth/confirm — confirm the email with the 6-digit code
 * Cognito sent. Returns void on success (HTTP 204).
 */
export async function confirmSignup(email: string, code: string): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/auth/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    });
  } catch (err) {
    throw new AuthError('NETWORK', err instanceof Error ? err.message : 'Network error', 0);
  }

  if (!response.ok) {
    let code: AuthError['code'] = 'INTERNAL_ERROR';
    let message = response.statusText || 'Confirmation failed';
    try {
      const body = (await response.json()) as ErrorResponse;
      message = body.error?.message ?? message;
      if (body.error?.code === 'INVALID_REQUEST') code = 'INVALID_REQUEST';
      if (body.error?.code === 'INVALID_CREDENTIALS') code = 'INVALID_CREDENTIALS';
    } catch { /* not JSON */ }
    throw new AuthError(code, message, response.status);
  }
}

// ---------------------------------------------------------------------------
// Refresh
// ---------------------------------------------------------------------------

export interface RefreshResult {
  token: string;
  refreshToken?: string;
  expiresIn: number;
}

interface RefreshResponseEnvelope { data: RefreshResult; }

/**
 * POST {API_BASE}/auth/refresh — exchange a refresh token for fresh
 * id/access tokens. Used by authFetch when the current id token has
 * expired (or is about to). Throws AuthError on 4xx so the caller can
 * branch (and authFetch can decide to log the user out).
 */
export async function refreshSession(refreshToken: string): Promise<RefreshResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
  } catch (err) {
    throw new AuthError('NETWORK', err instanceof Error ? err.message : 'Network error', 0);
  }

  if (!response.ok) {
    let code: AuthError['code'] = 'INTERNAL_ERROR';
    let message = response.statusText || 'Refresh failed';
    try {
      const body = (await response.json()) as ErrorResponse;
      message = body.error?.message ?? message;
      if (body.error?.code === 'INVALID_CREDENTIALS') code = 'INVALID_CREDENTIALS';
      if (body.error?.code === 'INVALID_REQUEST') code = 'INVALID_REQUEST';
    } catch { /* not JSON */ }
    throw new AuthError(code, message, response.status);
  }

  const body = (await response.json()) as RefreshResponseEnvelope;
  return body.data;
}

/**
 * POST {API_BASE}/auth/resend — re-send the verification code.
 * Always 204 (server doesn't leak account-existence).
 */
export async function resendCode(email: string): Promise<void> {
  try {
    await fetch(`${API_BASE}/auth/resend`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
  } catch {
    /* ignore — UI shows a generic 'sent' regardless to avoid enumeration */
  }
}
